import { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';

import { useGetCleanupBootstrapQuery } from '../store/api';
import type { WizardData } from '../types';
import { deriveWizardData } from '../utils/deriveWizardData';

export type CleanupWizardDataState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; data: WizardData };

/**
 * Data source seam for the cleanup wizard container (GDN-2295).
 *
 * Reads the live bootstrap endpoint (GDN-2296), whose payload matches the
 * pilot-fixture row shape exactly — so `deriveWizardData` consumes it
 * unchanged. This hook and the returned state shape are the stable seam;
 * Storybook and tests still feed `deriveWizardData` a fixture directly.
 */
export const useCleanupWizardData = (
  options: { orgId?: string; skip?: boolean } = {}
): CleanupWizardDataState => {
  const { orgId, skip = false } = options;

  // Condition tests orgId directly so it narrows to string in the query arg.
  const { data, isError } = useGetCleanupBootstrapQuery(
    !skip && orgId ? { orgId } : skipToken
  );

  return useMemo<CleanupWizardDataState>(() => {
    // Open without an org id: nothing to fetch, and the wizard can't load.
    if (!skip && !orgId) return { status: 'error' };
    if (isError) return { status: 'error' };
    if (!data) return { status: 'loading' };
    return { status: 'ready', data: deriveWizardData(data) };
  }, [data, isError, orgId, skip]);
};
